import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { DashboardLayout } from '../components/Layout'
import { Button, StatusBadge, Spinner, EmptyState } from '../components/UI'
import { Building2, TrendingUp, DollarSign, Eye, Calendar, MapPin, ArrowRight, Plus, BookOpen } from 'lucide-react'
import { format } from 'date-fns'

function StatCard({ icon, label, value, change, colorClass = 'blue', loading }) {
  return (
    <div className={`stat-card ${colorClass}`}>
      <div className={`stat-icon ${colorClass}`}>{icon}</div>
      {loading ? (
        <div className="skeleton" style={{ height: 36, width: 80, marginBottom: 8 }} />
      ) : (
        <div className="stat-value">{value}</div>
      )}
      <div className="stat-label">{label}</div>
      {change !== undefined && (
        <div className={`stat-change ${change >= 0 ? 'up' : 'down'}`}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% vs last month
        </div>
      )}
    </div>
  )
}

// ── Advertiser Dashboard ─────────────────────────────────────
function AdvertiserDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState({ total: 0, active: 0, spent: 0, spaces: 0 })
  const [recentBookings, setRecentBookings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const { data: bookings } = await supabase
        .from('bookings')
        .select('*, ad_spaces(title, area, type)')
        .eq('advertiser_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(5)

      if (bookings) {
        setRecentBookings(bookings)
        setStats({
          total: bookings.length,
          active: bookings.filter(b => b.status === 'active').length,
          spent: bookings.filter(b => b.payment_status === 'paid').reduce((s, b) => s + b.total_amount, 0),
          spaces: new Set(bookings.map(b => b.ad_space_id)).size,
        })
      }
      setLoading(false)
    }
    if (profile) load()
  }, [profile])

  return (
    <DashboardLayout title="Dashboard" actions={
      <Button icon={<MapPin size={16} />} onClick={() => navigate('/explore')}>Explore Map</Button>
    }>
      {/* Welcome banner */}
      <div style={{
        background: 'linear-gradient(135deg, var(--navy) 0%, var(--royal) 100%)',
        borderRadius: 'var(--radius-xl)',
        padding: '28px 32px',
        marginBottom: 28,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', right: 20, top: -20, fontSize: '8rem', opacity: 0.07, userSelect: 'none' }}>📍</div>
        <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Welcome back
        </div>
        <h2 style={{ fontSize: '1.6rem', fontWeight: 800, marginBottom: 8 }}>
          {profile?.full_name?.split(' ')[0] || 'Advertiser'} 👋
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', maxWidth: 400 }}>
          Find premium outdoor ad spaces across Dubai. Put your brand exactly where your audience is.
        </p>
        <button
          onClick={() => navigate('/explore')}
          style={{
            marginTop: 16, background: 'rgba(255,255,255,0.15)', color: 'white',
            border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '8px 18px',
            fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            transition: 'background 0.2s'
          }}
        >
          Browse Available Spaces <ArrowRight size={14} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 28 }}>
        <StatCard icon={<BookOpen size={20} />} label="Total Bookings" value={stats.total} colorClass="blue" loading={loading} />
        <StatCard icon={<TrendingUp size={20} />} label="Active Campaigns" value={stats.active} colorClass="green" loading={loading} />
        <StatCard icon={<DollarSign size={20} />} label="Total Spent (AED)" value={`${stats.spent.toLocaleString()}`} colorClass="orange" loading={loading} />
        <StatCard icon={<MapPin size={20} />} label="Unique Locations" value={stats.spaces} colorClass="purple" loading={loading} />
      </div>

      {/* Recent bookings */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>Recent Bookings</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')}>View All</Button>
        </div>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
        ) : recentBookings.length === 0 ? (
          <EmptyState
            icon={<BookOpen size={32} />}
            title="No bookings yet"
            description="Start by exploring available ad spaces on the map."
            action={<Button icon={<MapPin size={16} />} onClick={() => navigate('/explore')}>Explore Map</Button>}
          />
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map(b => (
                  <tr key={b.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{b.ad_spaces?.title}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin size={10} /> {b.ad_spaces?.area}
                      </div>
                    </td>
                    <td><span className="badge badge-neutral" style={{ textTransform: 'capitalize' }}>{b.ad_spaces?.type?.replace('_', ' ')}</span></td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>{b.start_date ? format(new Date(b.start_date), 'dd MMM') : '—'}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>to {b.end_date ? format(new Date(b.end_date), 'dd MMM yyyy') : '—'}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{b.total_amount?.toLocaleString()} AED</div>
                    </td>
                    <td><StatusBadge status={b.status} /></td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/bookings/${b.id}`)}>View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}

// ── Media Owner Dashboard ─────────────────────────────────────
function MediaOwnerDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState({ spaces: 0, bookings: 0, revenue: 0, pending: 0 })
  const [recentBookings, setRecentBookings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [spacesRes, bookingsRes] = await Promise.all([
        supabase.from('ad_spaces').select('id, is_available').eq('owner_id', profile.id),
        supabase.from('bookings').select('*, ad_spaces(title, area)').eq('owner_id', profile.id).order('created_at', { ascending: false }).limit(6)
      ])

      if (spacesRes.data) {
        setStats(prev => ({
          ...prev,
          spaces: spacesRes.data.length,
        }))
      }
      if (bookingsRes.data) {
        setRecentBookings(bookingsRes.data)
        setStats(prev => ({
          ...prev,
          bookings: bookingsRes.data.length,
          revenue: bookingsRes.data.filter(b => b.payment_status === 'paid').reduce((s, b) => s + (b.owner_payout || 0), 0),
          pending: bookingsRes.data.filter(b => b.status === 'inquiry').length,
        }))
      }
      setLoading(false)
    }
    if (profile) load()
  }, [profile])

  return (
    <DashboardLayout title="Dashboard" actions={
      <Button icon={<Plus size={16} />} onClick={() => navigate('/inventory/new')}>Add New Space</Button>
    }>
      {/* Welcome */}
      <div style={{
        background: 'linear-gradient(135deg, var(--navy) 0%, #1a3a6b 100%)',
        borderRadius: 'var(--radius-xl)', padding: '28px 32px', marginBottom: 28, color: 'white', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', right: 20, top: -10, fontSize: '8rem', opacity: 0.06, userSelect: 'none' }}>🏢</div>
        <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', marginBottom: 6, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Media Owner Portal</div>
        <h2 style={{ fontSize: '1.6rem', fontWeight: 800, marginBottom: 8 }}>{profile?.company_name || profile?.full_name}</h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
          {stats.pending > 0 ? `You have ${stats.pending} pending booking request${stats.pending > 1 ? 's' : ''} awaiting your response.` : 'Manage your ad inventory and track bookings.'}
        </p>
        {stats.pending > 0 && (
          <button onClick={() => navigate('/bookings')} style={{
            marginTop: 16, background: 'rgba(0,194,255,0.2)', color: 'var(--accent)',
            border: '1px solid rgba(0,194,255,0.3)', borderRadius: 8, padding: '8px 18px',
            fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
          }}>
            Review Requests <ArrowRight size={14} />
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 28 }}>
        <StatCard icon={<Building2 size={20} />} label="Total Spaces" value={stats.spaces} colorClass="blue" loading={loading} />
        <StatCard icon={<BookOpen size={20} />} label="Total Bookings" value={stats.bookings} colorClass="green" loading={loading} />
        <StatCard icon={<DollarSign size={20} />} label="Net Revenue (AED)" value={`${stats.revenue.toLocaleString()}`} colorClass="orange" loading={loading} />
        <StatCard icon={<Calendar size={20} />} label="Pending Requests" value={stats.pending} colorClass="purple" loading={loading} />
      </div>

      {/* Recent bookings table */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>Recent Booking Requests</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')}>View All</Button>
        </div>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
        ) : recentBookings.length === 0 ? (
          <EmptyState
            icon={<Building2 size={32} />}
            title="No bookings yet"
            description="Add your first ad space to start receiving booking requests."
            action={<Button icon={<Plus size={16} />} onClick={() => navigate('/inventory/new')}>Add Ad Space</Button>}
          />
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Space</th>
                  <th>Advertiser</th>
                  <th>Period</th>
                  <th>Payout</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map(b => (
                  <tr key={b.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{b.ad_spaces?.title}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{b.ad_spaces?.area}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>{b.brand_name || 'Unknown'}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.82rem' }}>
                        {b.start_date ? format(new Date(b.start_date), 'dd MMM') : '—'} – {b.end_date ? format(new Date(b.end_date), 'dd MMM yyyy') : '—'}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, color: 'var(--success)' }}>{b.owner_payout?.toLocaleString()} AED</div>
                    </td>
                    <td><StatusBadge status={b.status} /></td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/bookings/${b.id}`)}>
                        {b.status === 'inquiry' ? 'Review' : 'View'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}

// ── Main export ───────────────────────────────────────────────
export default function DashboardPage() {
  const { isMediaOwner } = useAuth()
  return isMediaOwner ? <MediaOwnerDashboard /> : <AdvertiserDashboard />
}
